import { motion } from 'framer-motion';
import { Mail } from 'lucide-react';

const offerings = [
  {
    label: 'A',
    name: 'Strategic Diagnostic & Market Clarity',
    description:
      'Identify the governing constraint, margin leakage, decision bottlenecks, and the clearest near-term growth and risk signals — including AI readiness gaps and intelligence infrastructure maturity.',
    outcomes: [
      'Constraint + bottleneck map',
      'Signal-driven risk/opportunity view',
      'AI readiness baseline assessment',
      'Decision clarity for "proceed / restructure / pause"',
    ],
  },
  {
    label: 'B',
    name: 'Commercial Architecture & Scaling Design',
    description:
      'Redesign go-to-market, sales and distribution mechanics, incentives, and decision structure so scaling becomes repeatable — with AI-native intelligence woven into operational workflows.',
    outcomes: [
      'Repeatable scaling mechanics',
      'Incentive alignment & decision flow redesign',
      'AI-integrated operational architecture',
      'Distribution strength + execution cadence',
    ],
  },
  {
    label: 'C',
    name: 'Long-Horizon Strategic Positioning',
    description:
      'Model multi-scenario futures, capital exposure, and expansion sequencing to build long-horizon resilience — including AI transformation roadmaps and machine intelligence integration.',
    outcomes: [
      'Scenario positioning map',
      'Capital sequencing logic',
      'AI transformation roadmap',
      'Expansion strategy that survives volatility',
    ],
  },
  {
    label: 'D',
    name: 'Continuous Strategic Intelligence Layer',
    description:
      'Ongoing external intelligence layer: drift detection, assumption refresh, signal monitoring, quarterly board packets, and continuous AI maturity assessment.',
    outcomes: [
      'Ongoing drift detection',
      'Quarterly board-ready intelligence briefs',
      'AI maturity progression tracking',
      'Adaptive strategy refresh rhythm',
    ],
  },
];

const principles = [
  'We do not produce generic strategy theatre',
  'We do not substitute narrative confidence for structural truth',
  'We do not recommend irreversible moves without constraint analysis',
  'We prioritise decision systems that survive scrutiny and time',
];

const ConsultancySection = () => {
  return (
    <section id="consultancy" className="relative py-24 lg:py-32" data-testid="consultancy-section">
      <div className="section-divider mb-20" />
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: 'rgba(201,162,39,0.6)' }}>
            Celestial Minds Strategic Advisory
          </p>
          <h2
            className="text-2xl md:text-3xl lg:text-4xl font-light tracking-[0.1em] text-white mb-6"
            style={{ fontFamily: "'Cinzel', serif" }}
            data-testid="consultancy-title"
          >
            Strategic Intelligence for Growth,
            <br className="hidden md:block" />
            Allocation & Market Resilience
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="max-w-3xl mx-auto text-center mb-20"
        >
          <p className="text-sm md:text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
            We help organisations scale revenue, strengthen distribution, allocate capital precisely, and stay ahead
            of market and regulatory shifts &mdash; through board-safe strategic intelligence, AI transformation strategy,
            and governance instruments designed for long-horizon resilience.
          </p>
        </motion.div>

        {/* What We Offer subtitle */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: 'rgba(201,162,39,0.5)' }}>
            Service Architecture
          </p>
          <h3
            className="text-xl md:text-2xl font-light tracking-[0.1em] text-white"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            What We Offer
          </h3>
        </motion.div>

        {/* Offering Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
          {offerings.map((offering, i) => {
            const outcomes = offering.outcomes;
            return (
            <motion.div
              key={offering.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <div
                className="card-glow relative overflow-hidden"
                style={{
                  background: 'rgba(10,22,40,0.4)',
                  border: '1px solid rgba(201,162,39,0.12)',
                  borderRadius: '4px',
                  padding: '28px',
                }}
                data-testid={`consultancy-offering-${offering.label.toLowerCase()}`}
              >
                <span
                  className="absolute top-3 right-4 text-5xl font-light select-none pointer-events-none"
                  style={{ color: 'rgba(201,162,39,0.06)', fontFamily: "'Cinzel', serif" }}
                >
                  {offering.label}
                </span>
                <h4
                  className="text-base md:text-lg tracking-wider text-white mb-4"
                  style={{ fontFamily: "'Cinzel', serif" }}
                >
                  {offering.name}
                </h4>
                <p className="text-sm leading-relaxed mb-5" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  {offering.description}
                </p>
                <p className="text-[10px] uppercase tracking-[0.2em] mb-3" style={{ color: 'rgba(201,162,39,0.5)' }}>
                  Client Outcomes
                </p>
                <ul className="space-y-2">
                  {outcomes.map((o) => (
                    <li key={o} className="flex items-start gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
                      <span className="mt-1 w-1 h-1 rounded-full shrink-0" style={{ background: '#c9a227' }} />
                      {o}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
            );
          })}
        </div>

        {/* Discipline & Restraint */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto mb-20"
        >
          <h3
            className="text-lg md:text-xl font-light tracking-[0.1em] text-white text-center mb-8"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Discipline & Restraint
          </h3>
          <ul className="space-y-4">
            {principles.map((r) => (
              <li
                key={r}
                className="flex items-start gap-3 text-sm"
                style={{ color: 'rgba(255,255,255,0.55)' }}
              >
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'rgba(201,162,39,0.4)' }} />
                {r}
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Request a Private Assessment CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center"
          data-testid="consultancy-cta"
        >
          <h3
            className="text-lg md:text-xl font-light tracking-[0.1em] text-white mb-4"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Request a Private Assessment
          </h3>
          <p className="text-sm leading-relaxed max-w-2xl mx-auto mb-8" style={{ color: 'rgba(255,255,255,0.5)' }}>
            Celestial Minds Strategic Advisory is not positioned as a general consultancy offering.
            We engage selectively with leadership teams navigating complex growth, capital allocation,
            AI transformation, and governance environments.
          </p>
          <a
            href="https://livinghighlevelintelligencesystem.com/enquiry"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2 text-xs uppercase tracking-widest mb-4"
            data-testid="consultancy-cta-link"
          >
            Request a Private Assessment
          </a>
          <p className="text-xs mt-4" style={{ color: 'rgba(255,255,255,0.35)' }}>
            <a
              href="mailto:invitation@celestialmindsfze.com"
              className="inline-flex items-center gap-1.5 hover:text-white/60 transition-colors"
              data-testid="consultancy-email-link"
            >
              <Mail className="w-3 h-3" />
              invitation@celestialmindsfze.com
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ConsultancySection;
